<template>
  <b-form-group label-class="d-flex align-items-center">
    <template slot="label">
      Enable Pay with Points
      <i class="guide-tooltip" id="redemption-guide"></i>
      <b-popover target="redemption-guide" triggers="hover"
        content="When enabled, this feature allows loyalty points to be used for payments on customer-facing platforms."></b-popover>
      <switches id="toggle-pay-with-points" v-model="is_pay_with_points_enabled" theme="custom" color="primary" type-bold="true"
        style="left: 10px; top: 10px"></switches>
    </template>
    <template v-if="is_pay_with_points_enabled">
      <b-form-group>
        <div class="form-container">
          <b-row no-gutters>
            <b-col>
              <b-row><b-col><span class="d-block mb-2 up-label">Points</span></b-col></b-row>
              <b-row no-gutters>
                <b-col cols="2">
                  <i class="icon d-block" style="margin-top: 9px;">
                    <img src="@/assets/images/mechanics/points.svg" alt="Points Icon"></i>
                </b-col>
                <b-col class="py-1">
                  <b-form-group>
                    <b-form-input v-model="form.pay_with_points_value" type="number" :formatter="pointsFormatter"
                      disabled></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
            <b-col cols="1">
              <i class="icon mx-auto d-block" style="margin-top: 4px;">
                <img src="@/assets/images/mechanics/convert.svg" alt="Convert Icon" style="margin-top: 36px;"></i>
            </b-col>
            <b-col cols="7">
              <b-row><b-col><span class="d-block mb-2 up-label">Currency</span></b-col></b-row>
              <b-row no-gutters>
                <b-col cols="1">
                  <i class="icon d-block" style="margin-top: 9px;">
                    <img src="@/assets/images/mechanics/currency.svg" alt="Currency Icon"></i>
                </b-col>
                <b-col class="p-1">
                  <b-form-select v-model="form.pay_with_points_currency" :class="{ 'text-muted': isSameAsMechanics }"
                    :options="currencyOptions" :disabled="isSameAsMechanics" />
                </b-col>
                <b-col class="p-1">
                  <b-form-group>
                    <div class="d-flex position-relative">
                      <b-form-input type="number" :class="{ 'text-muted': isSameAsMechanics }"
                        :formatter="pointsFormatter" placeholder="0" min="1" max="9999" maxlength="4" step="1"
                        v-model="form.pay_with_points_currency_value" :disabled="isSameAsMechanics"
                        ref="payWithPointsCurrency" @keypress.native="trimExcessMaxLength">
                      </b-form-input>
                      <div class="input-controls position-absolute d-inline-flex flex-column h-100">
                        <b-button class="value-control -up"
                          @click="controlValue('pay_with_points_currency_value', 'up', 'payWithPointsCurrency')"></b-button>
                        <b-button class="value-control -down"
                          @click="controlValue('pay_with_points_currency_value', 'down', 'payWithPointsCurrency')"></b-button>
                      </div>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
      </b-form-group>
      <b-form-group label="Applicable to specific channel/s">
        <b-form-checkbox-group id="channels-options" v-model="form.selectedChannels"
          :options="channelsOptions" name="channels-options"
          stacked></b-form-checkbox-group>
        <b-form-invalid-feedback :force-show="v$.selectedChannels.$invalid">
          <div v-for="typeError in v$.selectedChannels.$errors" :key="typeError.$uid">
            {{ typeError.$message }}
          </div>
        </b-form-invalid-feedback>
      </b-form-group>
    </template>
  </b-form-group>
</template>

<script>
import { defineComponent, getCurrentInstance, nextTick, onMounted, ref, watch } from "@vue/composition-api";
import Switches from "vue-switches-2";
import { useVuelidate } from "@vuelidate/core";
import { requiredIf, helpers } from "@vuelidate/validators";

export default defineComponent({
  props: ["setup", "isSameAsMechanics", "isPointsEarningEnabled"],
  components: { Switches },
  setup(props, { root, emit }) {
    const vm = getCurrentInstance();
    const is_pay_with_points_enabled = ref(false);
    const form = ref({
      pay_with_points_currency: "PHP",
      pay_with_points_currency_value: 0,
      pay_with_points_value: 0,
      selectedChannels: []
    });
    const currencyOptions = ["PHP", "USD"];
    const channelsOptions = [
      { value: "pay_with_points_estore_enabled", text: "eStore Web" },
      { value: "pay_with_points_sto_enabled", text: "Scan to Order" },
      { value: "pay_with_points_stp_enabled", text: "Scan to Pay" },
      { value: "pay_with_points_ca_enabled", text: "Customer App" }
    ];

    const pointsFormatter = (value, event) => {
      return event === null ? value : value * 1;
    }

    const controlValue = (field, direction, refI) => {
      const { min, max } = vm.proxy.$refs[refI];
      let valNum = Number(form.value[field]);

      if (direction === "up" && valNum < max) {
        form.value[field]++;
      } else if (direction === "down" && valNum >= min) {
        form.value[field]--;
      }
    };

    const trimExcessMaxLength = (evt) => {
      if (evt.target.value.length >= evt.target.maxLength) {
        evt.preventDefault();
      }
    }

    const fieldRequiredErrorMessage = "This field is required.";
    const rules = {
      selectedChannels: {
        required: helpers.withMessage(
          fieldRequiredErrorMessage,
          requiredIf(() => is_pay_with_points_enabled.value)
        )
      }
    };

    const v$ = useVuelidate(rules, form);

    onMounted(() => {
      initializeSetup();
      root.$on("validatePayWithPoints", () => {
        v$.value.$touch();
        nextTick(() => {
          if (!v$.value.$invalid) setPayWithPoints();
        });
      });
    });

    const initializeSetup = () => {
      const {
        pay_with_points_currency,
        pay_with_points_currency_value,
        pay_with_points_value,
      } = props.setup;

      setValues({ pay_with_points_currency, pay_with_points_currency_value, pay_with_points_value });

      is_pay_with_points_enabled.value = form.value.selectedChannels.length > 0;
    };

    const setValues = ({
      pay_with_points_currency,
      pay_with_points_currency_value,
      pay_with_points_value,
    }) => {
      form.value.pay_with_points_currency = pay_with_points_currency;
      form.value.pay_with_points_currency_value = pay_with_points_currency_value;
      form.value.pay_with_points_value = pay_with_points_value;

      channelsOptions
        .map(channel => channel.value)
        .forEach((channel) => {
          if (props.setup[channel] && !form.value.selectedChannels.includes(channel)) form.value.selectedChannels.push(channel);
        });
    };

    const setPayWithPoints = () => {
      const { pay_with_points_value, pay_with_points_currency, pay_with_points_currency_value, selectedChannels } = form.value;

      emit("setPayWithPoints", {
        pay_with_points_value,
        pay_with_points_currency,
        pay_with_points_currency_value,
        selectedChannels
      });
    };

    watch(
      () => props.setup,
      ({ pay_with_points_currency, pay_with_points_currency_value, pay_with_points_value }) => {
        setValues({ pay_with_points_currency, pay_with_points_currency_value, pay_with_points_value });
      }
    );

    watch(
      () => is_pay_with_points_enabled.value,
      (isEnabled) => {
        if (!isEnabled) {
          form.value.selectedChannels = [];
          channelsOptions
            .map(channel => channel.value)
            .forEach(channel => props.setup[channel] = false);
        } else if (isEnabled && props.isSameAsMechanics) {
          emit("getPointsMechanics", props.isSameAsMechanics, "pay_with_points");
        }
        v$.value.$reset();
      }
    );

    watch(
      () => props.isPointsEarningEnabled,
      (isEnabled) => {
        if (!isEnabled)
          is_pay_with_points_enabled.value = false;
      }
    );


    return {
      pointsFormatter,
      is_pay_with_points_enabled,
      form,
      currencyOptions,
      channelsOptions,
      v$,
      controlValue,
      trimExcessMaxLength
    }
  }
});
</script>

<style lang="scss" scoped>
.input-controls {
  right: 10px;
  justify-content: center;

  &>.btn {
    padding: 0;
    width: 9px;
    height: 6.5px;
    background: transparent url('~@/assets/images/spinner-control.png') no-repeat 100%;
    border: 0;
    box-shadow: none;
    margin: 1px 0;

    &:active,
    &:focus:active {
      background: transparent url('~@/assets/images/spinner-control.png') no-repeat 100%;
      box-shadow: none;
    }

    &.-down {
      transform: rotate(180deg);
    }
  }
}
</style>
